/**
 * Translation loader utility for lazy loading translation files
 * Provides global cache to avoid re-fetching and preloads English by default
 */ import { BUILD_ID } from 'config/environment';
import { logger } from 'services';
// Global cache for translation files
const translationCache = new Map();
// Track if English is currently being fetched to avoid duplicate fetches
let englishFetchPromise = null;
export const toCanonicalLocaleTag = (locale)=>{
    const [language, region, ...rest] = locale.split('-');
    if (!region) {
        return language.toLowerCase();
    }
    const canonicalRegion = /^\d+$/.test(region) ? region : region.toUpperCase();
    return [
        language.toLowerCase(),
        canonicalRegion,
        ...rest
    ].join('-');
};
/**
 * Fetches a translation file for a given locale
 * @param locale - The locale code (e.g., 'en', 'es', 'fr')
 * @returns Promise resolving to translation object
 */ export const fetchTranslation = async (locale)=>{
    // Return from cache if already loaded
    if (translationCache.has(locale)) {
        return translationCache.get(locale);
    }
    try {
        let data;
        if (process.env.REACT_APP_VERSIONED_ASSET_LAYOUT === 'true') {
            // CDN builds (EU): load via import() so webpack bundles translations as
            // JS chunks served via <script> tag — bypasses CORS entirely.
            // The shared CDN domain differs from each customer's app domain, making
            // every fetch() cross-origin and CORS-blocked for new accounts.
            const module = await import(`../translations/${locale}.json`);
            data = module.default || module;
        } else {
            // Legacy builds (US): fetch JSON file from same origin — no CORS issue.
            // Use BUILD_ID in filename for cache busting in production
            const canonicalLocale = toCanonicalLocaleTag(locale);
            const filename = BUILD_ID ? `${canonicalLocale}.${BUILD_ID}.json` : `${canonicalLocale}.json`;
            const response = await fetch(`/translations/${filename}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch translation: ${response.statusText}`);
            }
            data = await response.json();
        }
        translationCache.set(locale, data);
        return data;
    } catch (error) {
        logger.error(`Failed to load translation for locale: ${locale}`, {
            error
        });
        // Fallback to English if not already English
        if (locale !== 'en') {
            return fetchTranslation('en');
        }
        // Return empty object as last resort
        return {};
    }
};
/**
 * Gets the translation cache Map
 * Useful for non-async contexts that need immediate access
 */ export const getTranslationCache = ()=>translationCache;
/**
 * Preloads English translations immediately
 * This ensures English is available as soon as possible
 * Returns a promise that resolves when English is loaded
 */ export const preloadEnglish = ()=>{
    if (!englishFetchPromise) {
        englishFetchPromise = fetchTranslation('en').then((translations)=>{
            englishFetchPromise = null;
            return translations;
        }).catch((error)=>{
            logger.error('Failed to preload English translations', {
                error
            });
            englishFetchPromise = null;
            return {};
        });
    }
    return englishFetchPromise;
};
/**
 * Eagerly preload English translations when this module is loaded
 * This minimizes the race condition window by starting the fetch immediately
 *
 * Skip in test environments — setupTests.tsx pre-seeds the cache synchronously
 * before MSW is started, so this fetch would fire before any request interceptor
 * is active and always fail with a noisy "Failed to load translation" error.
 */ if (typeof process === 'undefined' || process.env.NODE_ENV !== 'test') {
    preloadEnglish();
}
